import { useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Table, type Column } from "@/components/Table";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { Loaded } from "@/components/Loading";
import { LegalReader } from "./LegalReader";
import { SubProcessorsPanel } from "./SubProcessorsPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import { rowStatus, ROW_STATUS_KEY, ROW_STATUS_TONE } from "./status";
import type { LegalStatus, LegalDocumentStatus } from "@/types";

/**
 * The institution's legal file.
 *
 * Which documents apply is decided by country and segment — not by us and not
 * by the institution. A German school sees an AVV; a studio in the United
 * States does not, and neither of them chose that.
 */
export function LegalPage() {
  const t = useT();
  const { locale } = useI18n();
  const { institution } = useAuth();
  const status = useCallable<LegalStatus>(CALLABLES.getLegalStatus);
  const [reading, setReading] = useState<LegalDocumentStatus | undefined>(undefined);

  const columns: readonly Column<LegalDocumentStatus>[] = [
    {
      key: "title",
      headerKey: "legal.column.document",
      render: (document) => (
        <span className="font-medium text-ink">{document.title}</span>
      ),
    },
    {
      key: "version",
      headerKey: "legal.column.version",
      width: "10rem",
      render: (document) => (
        <span className="text-ink-muted">
          {document.version}
          {document.acceptedVersion !== undefined &&
          document.acceptedVersion !== document.version ? (
            <span className="ml-2 text-xs">
              {t("legal.acceptedVersion", { version: document.acceptedVersion })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "status",
      headerKey: "legal.column.status",
      width: "14rem",
      render: (document) => {
        const shown = rowStatus(document);
        return <span className={ROW_STATUS_TONE[shown]}>{t(ROW_STATUS_KEY[shown])}</span>;
      },
    },
    {
      /*
        Its own column, with a header. Squeezed in beside the button it was a
        headerless date that wrapped to three lines — "29 / Aug / 2026" — and
        nothing said what it was the date of (live run, 2026-09-09).
      */
      key: "published",
      headerKey: "legal.column.published",
      width: "9rem",
      render: (document) => (
        <span className="whitespace-nowrap text-ink-muted">
          {formatDate(locale, document.publishedAt)}
        </span>
      ),
    },
    {
      key: "actions",
      headerKey: "legal.column.actions",
      align: "right",
      render: (document) => (
        <div className="flex items-center justify-end">
          <Button variant="secondary" onClick={() => setReading(document)}>
            {document.accepted || !document.required ? t("legal.read") : t("legal.accept")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <SectionShell titleKey="legal.title">
      <p className="max-w-2xl text-ink-muted">{t("legal.intro")}</p>

      <div className="mt-8">
        <Loaded state={status}>
          {(loaded) => (
            <>
              <Table
                columns={columns}
                rows={loaded.documents}
                rowKey={(document) => document.id}
                caption="legal.title"
              />

              {loaded.gaps.length > 0 ? (
                /*
                  Our failure, not theirs, and it says so. An owner who sees a
                  required document they cannot find should not spend an
                  afternoon looking for the button.
                */
                <div className="mt-8 rounded-card border border-warning bg-warning-soft px-5 py-4">
                  <p className="font-medium text-ink">{t("legal.gapsTitle")}</p>
                  <p className="mt-1 text-sm text-ink-soft">{t("legal.gapsBody")}</p>
                  <ul className="mt-3 list-disc pl-5 text-sm text-ink-soft">
                    {loaded.gaps.map((gap) => (
                      <li key={gap}>{t(`legal.docType.${gap}`)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </Loaded>
      </div>

      <div className="mt-12 max-w-2xl">
        <SubProcessorsPanel />
      </div>

      <Modal
        open={reading !== undefined}
        onClose={() => setReading(undefined)}
        titleKey="legal.read"
      >
        {reading ? (
          <LegalReader
            document={reading}
            institutionName={institution?.name ?? ""}
            onAccepted={() => {
              setReading(undefined);
              status.reload();
            }}
            onClose={() => setReading(undefined)}
          />
        ) : null}
      </Modal>
    </SectionShell>
  );
}
